import { FormHelperText, InputAdornment, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  APPOINTMENT_DATEPICKER_LABEL,
  PICKUP_DATEPICKER_LABEL,
  RETURN_TRIP_PICKUP_DATEPICKER_LABEL,
} from '../constants';
import { parseWallClockDateTime } from './utils';

AppointmentDateTimePicker.propTypes = {
  label: PropTypes.string,
  appointmentDateTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  setter: PropTypes.func,
  field: PropTypes.string,
  restrictedDates: PropTypes.array,
  isLoading: PropTypes.bool,
  setIsAllowedDate: PropTypes.func,
  referenceDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  viewOnly: PropTypes.bool,
  isNewRequest: PropTypes.bool,
};

// All transport dates/times are handled as EST wall-clock values for now, regardless of the user's browser timezone.
const DISPLAY_TIMEZONE_LABEL = 'EST';
const dateTimePickerSlotProps = {
  textField: { size: 'small', sx: { width: '100%' } },
  actionBar: { actions: ['clear', 'cancel', 'accept'] },
  layout: {
    sx: {
      '.MuiMultiSectionDigitalClockSection-item': {
        width: 64,
        justifyContent: 'center',
      },
      '.MuiMultiSectionDigitalClockSection-root': {
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { borderRadius: '2px', backgroundColor: 'rgba(0,0,0,0.2)' },
      },
    },
  },
};

// Helper to check if a date/time is restricted
const isDateTimeRestricted = (date, restrictedDates) => {
  if (!restrictedDates || restrictedDates.length === 0) return false;

  // The date picker provides EST wall-clock values (no timezone conversion is applied).
  const localTime = date instanceof Date ? date : new Date(date);

  return restrictedDates.some((rd) => {
    // Restricted dates are also plain EST wall-clock values.
    const startUtc = parseWallClockDateTime(rd.startDate, rd.startTime);
    const endUtc = parseWallClockDateTime(rd.endDate, rd.endTime);

    // Compare the wall-clock values directly.
    return localTime >= startUtc && localTime <= endUtc;
  });
};

// Helper to check if a whole day is fully restricted (all hours)
const isDayFullyRestricted = (date, restrictedDates) => {
  if (!restrictedDates || restrictedDates.length === 0) return false;

  // Get the start and end of the day in local time
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const localDayStart = new Date(year, month, day, 0, 0, 0, 0);
  const localDayEnd = new Date(year, month, day, 23, 59, 59, 999);

  return restrictedDates.some((rd) => {
    // Restricted dates are plain EST wall-clock values (no timezone conversion is applied).
    const startUtc = parseWallClockDateTime(rd.startDate, rd.startTime);
    const endUtc = parseWallClockDateTime(rd.endDate, rd.endTime);

    // Check if the restricted range covers the entire local day
    // The restriction must start at or before the local day start
    // and end at or after the local day end
    return startUtc <= localDayStart && endUtc >= localDayEnd;
  });
};

const getEasternNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));

const isPastDate = (date) => {
  const now = getEasternNow();
  const selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return selectedDate < currentDate;
};

const isPastDateTime = (date) => date && new Date(date).getTime() < getEasternNow().getTime();

export default function AppointmentDateTimePicker({
  label,
  appointmentDateTime,
  setter,
  field,
  restrictedDates,
  isLoading = false,
  setIsAllowedDate,
  referenceDate,
  viewOnly = false,
  isNewRequest = false,
}) {
  const [error, setError] = useState('');
  const APPOINTMENT_BEFORE_PICKUP_ERROR = 'Appointment time cannot be before pickup time.';
  const PICKUP_AFTER_APPOINTMENT_ERROR = 'Pickup time cannot be after appointment time.';
  const RETURN_PICKUP_BEFORE_APPOINTMENT_ERROR = 'Return pickup time cannot be before appointment time.';

  // Helper: validate chronological constraint between pickup and appointment times
  const checkTimeConstraint = (value) => {
    if (referenceDate && value && isNewRequest) {
      const currentTime = new Date(value);
      const refTime = new Date(referenceDate);

      // If this is the Appointment picker, referenceDate is pickupTime
      // Constraint: appointmentTime must be > pickupTime
      if (label && label === APPOINTMENT_DATEPICKER_LABEL) {
        if (currentTime < refTime) {
          return { isValid: false, message: APPOINTMENT_BEFORE_PICKUP_ERROR };
        }
      }

      // If this is the Pickup picker, referenceDate is appointmentTime
      // Constraint: pickupTime must be < appointmentTime
      if (label && label === PICKUP_DATEPICKER_LABEL) {
        if (currentTime > refTime) {
          return { isValid: false, message: PICKUP_AFTER_APPOINTMENT_ERROR };
        }
      }

      // If this is the Return Pickup picker, referenceDate is appointmentTime
      // Constraint: return pickupTime must be > appointmentTime
      if (label && label === RETURN_TRIP_PICKUP_DATEPICKER_LABEL) {
        if (currentTime < refTime) {
          return { isValid: false, message: RETURN_PICKUP_BEFORE_APPOINTMENT_ERROR };
        }
      }
    }
    return { isValid: true, message: '' };
  };

  // Validate pickup / appointment time chronological constraint for the other when one changes
  useEffect(() => {
    if (viewOnly) {
      setError('');
      setIsAllowedDate?.(true);
      return;
    }

    const validation = checkTimeConstraint(appointmentDateTime);
    const isChronologicalError = [
      APPOINTMENT_BEFORE_PICKUP_ERROR,
      PICKUP_AFTER_APPOINTMENT_ERROR,
      RETURN_PICKUP_BEFORE_APPOINTMENT_ERROR,
    ].includes(error);

    if (isChronologicalError && validation.isValid) {
      setError('');
      setIsAllowedDate?.(true);
    }
  }, [appointmentDateTime, referenceDate, label, viewOnly]);

  return (
    <>
      <DateTimePicker
        disabled={viewOnly}
        closeOnSelect={false}
        label={label}
        loading={isLoading}
        slotProps={{
          ...dateTimePickerSlotProps,
          textField: {
            ...dateTimePickerSlotProps.textField,
            error: Boolean(error),
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {DISPLAY_TIMEZONE_LABEL}
                  </Typography>
                </InputAdornment>
              ),
            },
          },
          desktopPaper: {
            sx: {
              maxHeight: 400,
            },
          },
        }}
        shouldDisableDate={(date) => (isNewRequest && isPastDate(date)) || isDayFullyRestricted(date, restrictedDates)}
        shouldDisableTime={(timeValue) =>
          (isNewRequest && isPastDateTime(timeValue)) || isDateTimeRestricted(timeValue, restrictedDates)
        }
        value={appointmentDateTime || null}
        referenceDate={referenceDate || null}
        onChange={(v) => {
          if (viewOnly) {
            setter(field, v);
            return;
          }

          // // Check for past date
          if (v && isNewRequest) {
            if (isPastDateTime(v)) {
              setError('Cannot select a past date or time.');
              setIsAllowedDate?.(false);
              setter(field, v);
              return;
            }
          }
          // Check chronological constraint
          const validation = checkTimeConstraint(v);
          if (v && !validation.isValid) {
            setError(validation.message);
            setIsAllowedDate?.(false);
            setter(field, v);
            return;
          }
          setter(field, v);
          setIsAllowedDate?.(true);
          setError('');
        }}
        disableAcceptOnInvalid
        onError={(reason, value) => {
          if (viewOnly) {
            setError('');
            return;
          }

          // Show error for restricted date/time
          if (value && (isDayFullyRestricted(value, restrictedDates) || isDateTimeRestricted(value, restrictedDates))) {
            setError('Selected time is restricted. Please choose another time.');
            setIsAllowedDate?.(false);
            return;
          }
          // Show error for past date/time
          if (isNewRequest && (reason === 'disablePast' || (value && isPastDateTime(value)))) {
            setError('Cannot select a past date or time.');
            setIsAllowedDate?.(false);
            return;
          }
          setError('');
        }}
        inputProps={{ 'aria-label': 'Appointment Time Picker' }}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </>
  );
}
